import { Navigate, Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Inbox, FileEdit, FileText, Database, Newspaper, Calendar, Mic, Settings, LogOut, ChevronLeft, Image, Sparkles, Activity, BookOpen } from "lucide-react";

const sidebarLinks = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/admin" },
  { label: "Signals Inbox", icon: Inbox, path: "/admin/signals" },
  { label: "AI Pulse", icon: Activity, path: "/admin/pulse" },
  { label: "Drafts", icon: FileEdit, path: "/admin/drafts" },
  { label: "Articles", icon: FileText, path: "/admin/articles" },
  { label: "Sources", icon: Database, path: "/admin/sources" },
  { label: "Newsletter", icon: Newspaper, path: "/admin/newsletter" },
  { label: "Calendar", icon: Calendar, path: "/admin/calendar" },
  { label: "Inquiries", icon: Mic, path: "/admin/inquiries" },
  { label: "Reports", icon: BookOpen, path: "/admin/reports" },
  { label: "Media Kit", icon: Image, path: "/admin/media" },
  { label: "Voice Profile", icon: Sparkles, path: "/admin/voice" },
  { label: "Automation", icon: Settings, path: "/admin/automation" },
];

const isActivePath = (pathname, path) =>
  path === "/admin" ? pathname === "/admin" || pathname === "/admin/" : pathname === path || (path === "/admin/articles" && pathname.startsWith("/admin/articles"));

const AdminLayout = () => {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) return <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center"><div className="text-[var(--text-secondary)]">Loading...</div></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[var(--bg-secondary)] border-r border-[rgba(124,58,237,0.1)] flex flex-col fixed h-full z-40" data-testid="admin-sidebar">
        <div className="p-5 border-b border-[rgba(124,58,237,0.1)]">
          <Link to="/" className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-white text-xs mb-3 transition-colors" data-testid="admin-back-to-site">
            <ChevronLeft className="w-3 h-3" /> Back to Site
          </Link>
          <h2 className="font-brand text-sm uppercase tracking-[0.15em] text-white">Raulvin</h2>
          <p className="text-xs text-[var(--accent-violet-light)] mt-0.5">Intelligence Desk</p>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {sidebarLinks.map((link) => {
            const isActive = isActivePath(location.pathname, link.path);
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center gap-3 px-5 py-2.5 text-sm transition-all mx-2 rounded-lg mb-0.5 ${
                  isActive
                    ? "bg-[var(--accent-violet)]/15 text-[var(--accent-violet-light)] font-medium"
                    : "text-[var(--text-secondary)] hover:text-white hover:bg-white/5"
                }`}
                data-testid={`sidebar-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[rgba(124,58,237,0.1)]">
          <div className="flex items-center gap-3 mb-3">
            <img src="/logo-rc-sm.png" alt="RC" className="w-9 h-auto" />
            <div>
              <p className="text-xs text-white font-medium" data-testid="admin-user-name">{user.name || user.email}</p>
              <p className="text-[10px] text-[var(--text-secondary)]">{user.role}</p>
            </div>
          </div>
          <button onClick={logout} className="flex items-center gap-2 text-xs text-[var(--text-secondary)] hover:text-red-400 transition-colors w-full" data-testid="admin-logout">
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
